/**
 * LocalStorage Service for browser storage operations
 *
 * This service provides a centralized interface for all localStorage operations
 * with proper error handling, logging, and type safety.
 */
import { logger } from './logger';

class LocalStorageService {
  /**
   * Get an item from localStorage
   * @param {string} key - The storage key
   * @param {any} defaultValue - Default value if key doesn't exist or parsing fails
   * @returns {any} The parsed value or defaultValue
   */
  static getItem(key, defaultValue = null) {
    try {
      logger.info('[LocalStorageService]', `Getting item: ${key}`);
      const item = localStorage.getItem(key);

      if (item === null) {
        logger.info('[LocalStorageService]', `Key "${key}" not found, returning default value`);
        return defaultValue;
      }

      // Try to parse JSON, if it fails return the raw string
      try {
        const parsed = JSON.parse(item);
        logger.info('[LocalStorageService]', `Successfully parsed JSON for key: ${key}`);
        return parsed;
      } catch (parseError) {
        // Not JSON, return as string
        logger.info('[LocalStorageService]', `Key "${key}" is not JSON, returning as string`);
        return item;
      }
    } catch (error) {
      logger.error('[LocalStorageService]', `Error getting item "${key}" from localStorage`, error);
      return defaultValue;
    }
  }

  /**
   * Set an item in localStorage
   * @param {string} key - The storage key
   * @param {any} value - The value to store (will be stringified if object)
   * @returns {boolean} Success status
   */
  static setItem(key, value) {
    try {
      logger.info('[LocalStorageService]', `Setting item: ${key}`);

      // Convert value to string if it's an object
      const stringValue = typeof value === 'object' ? JSON.stringify(value) : String(value);

      localStorage.setItem(key, stringValue);
      logger.info('[LocalStorageService]', `Successfully set item: ${key}`);
      return true;
    } catch (error) {
      logger.error('[LocalStorageService]', `Error setting item "${key}" in localStorage`, error);
      // Check for quota exceeded error
      if (error.name === 'QuotaExceededError') {
        logger.error('[LocalStorageService]', 'localStorage quota exceeded');
      }
      return false;
    }
  }

  /**
   * Remove an item from localStorage
   * @param {string} key - The storage key
   * @returns {boolean} Success status
   */
  static removeItem(key) {
    try {
      logger.info('[LocalStorageService]', `Removing item: ${key}`);
      localStorage.removeItem(key);
      logger.info('[LocalStorageService]', `Successfully removed item: ${key}`);
      return true;
    } catch (error) {
      logger.error('[LocalStorageService]', `Error removing item "${key}" from localStorage`, error);
      return false;
    }
  }

  /**
   * Clear all items from localStorage
   * @returns {boolean} Success status
   */
  static clear() {
    try {
      logger.warn('[LocalStorageService]', 'Clearing all localStorage items');
      localStorage.clear();
      logger.info('[LocalStorageService]', 'Successfully cleared all localStorage items');
      return true;
    } catch (error) {
      logger.error('[LocalStorageService]', 'Error clearing localStorage', error);
      return false;
    }
  }

  /**
   * Check if a key exists in localStorage
   * @param {string} key - The storage key
   * @returns {boolean} Whether the key exists
   */
  static hasItem(key) {
    try {
      const exists = localStorage.getItem(key) !== null;
      logger.info('[LocalStorageService]', `Checking if key "${key}" exists: ${exists}`);
      return exists;
    } catch (error) {
      logger.error('[LocalStorageService]', `Error checking if key "${key}" exists`, error);
      return false;
    }
  }

  /**
   * Get all keys from localStorage
   * @returns {string[]} Array of storage keys
   */
  static getAllKeys() {
    try {
      const keys = Object.keys(localStorage);
      logger.info('[LocalStorageService]', `Found ${keys.length} keys in localStorage`);
      return keys;
    } catch (error) {
      logger.error('[LocalStorageService]', 'Error getting localStorage keys', error);
      return [];
    }
  }

  /**
   * Get the size of data stored in localStorage
   * @returns {number} Approximate size in bytes
   */
  static getStorageSize() {
    try {
      let size = 0;
      for (const key in localStorage) {
        if (localStorage.hasOwnProperty(key)) {
          size += localStorage[key].length + key.length;
        }
      }
      logger.info('[LocalStorageService]', `Total localStorage size: ${size} bytes`);
      return size;
    } catch (error) {
      logger.error('[LocalStorageService]', 'Error calculating localStorage size', error);
      return 0;
    }
  }

  /**
   * Check if localStorage is available
   * @returns {boolean} Whether localStorage is available
   */
  static isAvailable() {
    try {
      const testKey = '__localStorage_test__';
      localStorage.setItem(testKey, 'test');
      localStorage.removeItem(testKey);
      return true;
    } catch (error) {
      logger.error('[LocalStorageService]', 'localStorage is not available', error);
      return false;
    }
  }
}

export default LocalStorageService;

// Re-export commonly used methods for convenience
export const {
  getItem,
  setItem,
  removeItem,
  clear,
  hasItem,
  getAllKeys,
  getStorageSize,
  isAvailable
} = LocalStorageService;
